import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

const svg1 = `<svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" version="1.1" viewBox="159.56 304.13 560.8 529.31"><defs><style>.st0 {fill: #1e1c1f;}</style></defs><path class="st0" d="M381.09,717.68c-31.92,0-57.88,25.96-57.88,57.88s25.96,57.88,57.88,57.88,57.88-25.96,57.88-57.88-25.96-57.88-57.88-57.88ZM381.09,813.44c-20.89,0-37.88-16.99-37.88-37.88s16.99-37.88,37.88-37.88,37.88,16.99,37.88,37.88-16.99,37.88-37.88,37.88Z"/><path class="st0" d="M593.37,717.68c-31.92,0-57.88,25.96-57.88,57.88s25.96,57.88,57.88,57.88,57.88-25.96,57.88-57.88-25.96-57.88-57.88-57.88ZM593.37,813.44c-20.89,0-37.88-16.99-37.88-37.88s16.99-37.88,37.88-37.88,37.88,16.99,37.88,37.88-16.99,37.88-37.88,37.88Z"/><path class="st0" d="M718.48,446.38c-1.88-2.61-4.9-4.16-8.12-4.16h-68.14c.02-1.05.04-2.11.04-3.17,0-74.4-60.52-134.92-134.92-134.92s-134.92,60.52-134.92,134.92c0,1.06.02,2.12.04,3.17h-70.56c-.73,0-1.43.08-2.12.23l-26.4-130.3c-.94-4.66-5.04-8.01-9.8-8.01h-94.02c-5.52,0-10,4.48-10,10s4.48,10,10,10h85.84l26.36,130.08c.01.05.02.1.03.16l50.94,232.38c1.01,4.59,5.07,7.86,9.77,7.86h289.76c4.29,0,8.11-2.74,9.48-6.81l78.1-232.38c1.03-3.05.52-6.41-1.36-9.02ZM507.34,324.13c63.37,0,114.92,51.55,114.92,114.92s-51.55,114.92-114.92,114.92-114.92-51.55-114.92-114.92,51.55-114.92,114.92-114.92ZM625.07,674.6h-274.53l-46.56-212.38h70.43c11.02,63.39,66.43,111.75,132.92,111.75s121.9-48.36,132.92-111.75h56.19l-71.38,212.38Z"/><path class="st0" d="M480.18,489.99c5.45,5.45,14.29,5.45,19.74,0l75.71-75.71c3.79-3.79,4.25-9.96.7-13.97-3.86-4.35-10.52-4.5-14.57-.45l-65.68,65.69c-3.33,3.33-8.71,3.32-12.03,0l-33.23-33.28c-3.79-3.79-9.96-4.26-13.97-.71-4.35,3.86-4.51,10.52-.46,14.57l43.79,43.86Z"/></svg>`;
const svg2 = `<svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" version="1.1" viewBox="62.09 314 810.15 379.17"><defs><style>.st0 {fill: #1e1c1f;}</style></defs><path class="st0" d="M640.04,314H162.3c-5.52,0-10,4.48-10,10v87.75h-41.01c-5.52,0-10,4.48-10,10s4.48,10,10,10h41.01v36.25h-80.21c-5.52,0-10,4.48-10,10s4.48,10,10,10h80.21v140.09c0,5.52,4.48,10,10,10h81.17c6.96,31.47,35.07,55.08,68.6,55.08s61.63-23.61,68.6-55.08h259.37c5.52,0,10-4.48,10-10v-304.09c0-5.52-4.48-10-10-10ZM312.07,673.17c-27.72,0-50.27-22.55-50.27-50.27s22.55-50.27,50.27-50.27,50.27,22.55,50.27,50.27-22.55,50.27-50.27,50.27ZM630.04,618.09h-247.88c-2.48-36.51-32.96-65.46-70.09-65.46s-67.6,28.95-70.09,65.46h-69.68v-130.09h94.41c5.52,0,10-4.48,10-10s-4.48-10-10-10h-94.41v-36.25h94.41c5.52,0,10-4.48,10-10s-4.48-10-10-10h-94.41v-77.75h457.74v284.09Z"/><path class="st0" d="M865.63,466.63l-180.15-65.08c-3.06-1.11-6.48-.65-9.14,1.22-2.67,1.87-4.25,4.93-4.25,8.18v217.13c0,5.52,4.48,10,10,10h21.97c6.96,31.47,35.07,55.08,68.6,55.08s61.63-23.61,68.6-55.08h20.98c5.52,0,10-4.48,10-10v-152.05c0-4.21-2.64-7.97-6.6-9.41ZM772.65,673.17c-27.72,0-50.27-22.55-50.27-50.27s22.55-50.27,50.27-50.27,50.27,22.55,50.27,50.27-22.55,50.27-50.27,50.27ZM852.23,618.09h-9.49c-2.48-36.51-32.96-65.46-70.09-65.46s-67.6,28.95-70.09,65.46h-10.48v-192.88l160.15,57.85v135.03Z"/></svg>`;
const svg3 = `<svg xmlns="http://www.w3.org/2000/svg" id="Layer_1" version="1.1" viewBox="261.61 263.72 486.09 472.54"><defs><style>.st0 {fill: #1e1c1f;}</style></defs><path class="st0" d="M714.09,512.37v-95.94c0-.39-.01-.79-.05-1.18,0-.02,0-.04,0-.07-.02-.22-.06-.43-.1-.65-.03-.17-.06-.34-.09-.5-.04-.2-.09-.39-.15-.58-.05-.19-.12-.37-.18-.56-.06-.17-.12-.35-.19-.52-.13-.32-.28-.63-.45-.94-.02-.04-.03-.08-.05-.11l-79.85-142.49c-1.77-3.16-5.11-5.11-8.72-5.11h-262.36c-3.43,0-6.61,1.75-8.45,4.65l-90.29,142.49s-.05.1-.08.15c-.18.29-.34.59-.49.9-.04.07-.07.15-.11.22-.17.38-.32.76-.44,1.16,0,0,0,.01,0,.02-.11.37-.2.75-.27,1.14-.02.11-.03.22-.05.32-.04.29-.07.59-.09.89,0,.12-.02.23-.02.35,0,.06,0,.12,0,.18v278.58c0,5.52,4.48,10,10,10h254.64c23.44,19.64,53.63,31.49,86.53,31.49,74.4,0,134.92-60.52,134.92-134.92,0-34.07-12.7-65.22-33.6-88.99ZM687.03,406.21h-189.18v-122.49h120.54l68.64,122.49ZM367.39,283.72h110.46v122.49h-188.07l77.62-122.49ZM281.6,426.21h412.49v67.56c-22.63-17.15-50.81-27.33-81.32-27.33-74.39,0-134.92,60.52-134.92,134.92,0,31.47,10.83,60.46,28.96,83.43h-225.21v-258.58ZM612.77,716.28c-63.37,0-114.92-51.55-114.92-114.92s51.55-114.92,114.92-114.92,114.92,51.55,114.92,114.92-51.55,114.92-114.92,114.92Z"/><path class="st0" d="M667.19,562.17l-65.68,65.69c-3.33,3.33-8.71,3.32-12.03,0l-33.23-33.28c-3.79-3.79-9.96-4.26-13.97-.71-4.35,3.86-4.51,10.52-.46,14.57l43.79,43.86c5.45,5.45,14.29,5.45,19.74,0l75.71-75.71c3.79-3.79,4.25-9.96.7-13.97-3.86-4.35-10.52-4.5-14.57-.45Z"/><path class="st0" d="M457.16,459.86h-125.53c-5.52,0-10,4.48-10,10s4.48,10,10,10h125.53c5.52,0,10-4.48,10-10s-4.48-10-10-10Z"/><path class="st0" d="M396.87,504.44h-65.24c-5.52,0-10,4.48-10,10s4.48,10,10,10h65.24c5.52,0,10-4.48,10-10s-4.48-10-10-10Z"/></svg>`;
function replaceSVG() {
    document.querySelectorAll(".ordered-content-box__step-box .icon__content-box")
        .forEach(el => el.innerHTML = svg1);
    document.querySelectorAll(".order-ready-content-box__step-box .icon__content-box")
        .forEach(el => el.innerHTML = svg2);
    document.querySelectorAll(".order-delivered-content-box__step-box .icon__content-box")
        .forEach(el => el.innerHTML = svg3);
}
setInterval(replaceSVG, 10);
replaceSVG();
